import {type ApolloError} from '@apollo/client';

import type * as RunSetTypes from '../../state/views/runSet/types';
import {useDebounceState} from '../../util/hooks';
import {useMaybeWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {
  type MetricKeySearchResult,
  useMetricKeySearch,
} from './useMetricKeySearch';

const SEARCH_DEBOUNCE_MS = 400;
const EMPTY_RUN_SET_REFS: RunSetTypes.Ref[] = [];

/**
 * Search-as-you-type state for a metric picker.
 *
 * Owns the debounce, the query, and the loading rule, so the pickers built on
 * it only decide how to present the results. `MetricSearchDropdown` renders
 * them with semantic-ui; the media settings drawer uses react-select with its
 * own option rendering and drives the same state directly.
 *
 * Run set refs come from `WorkspaceRefsContext` rather than props: every metric
 * picker resolves them from that context anyway, some by prop-drilling it down.
 * Surfaces without the context (reports) get no results rather than a throw,
 * and are expected to render a non-metric-search picker instead.
 */
export function useMetricSearchState({
  types,
  resultLimit,
}: {
  /** Any-match membership on `MetricInfo.types` (e.g. `['number']`). */
  types?: string[];
  resultLimit: number;
}): {
  results: MetricKeySearchResult[];
  hasMore: boolean;
  isLoading: boolean;
  error: ApolloError | undefined;
  retry: () => void;
  /** Current raw input, for controlled search fields. */
  searchQuery: string;
  /** Updates the search; call with '' to reset. */
  setSearch: (value: string) => void;
} {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const [rawSearch, debouncedSearch, setSearch] = useDebounceState(
    '',
    SEARCH_DEBOUNCE_MS
  );

  const {results, hasMore, loading, error, retry} = useMetricKeySearch(
    workspaceRefs?.runSetRefs ?? EMPTY_RUN_SET_REFS,
    workspaceRefs?.customRunNamesRef,
    {
      types,
      metricNamePattern: debouncedSearch || undefined,
      pageSize: resultLimit,
      skip: workspaceRefs == null,
    }
  );

  return {
    results,
    hasMore,
    // Treat the debounce window as loading so the spinner shows while typing
    // rather than briefly showing stale results as if they were final.
    isLoading: loading || rawSearch !== debouncedSearch,
    error,
    retry,
    searchQuery: rawSearch,
    setSearch,
  };
}
